import React, { useEffect, useRef, useState } from 'react';

const MISSED_CALL_SMS_MSG =
  "Hi, you just tried reaching us — sorry we missed you. Reply here or tap to book a time and we'll get right back to you.";
const ADMIN_LOSS_AMOUNTS = ['-$2', '-$5', '-$8', '-$3', '-$12', '-$6', '-$15', '-$4', '-$10', '-$7'];

const LAPSED_ROWS = [
  {
    initials: 'JK',
    multiline: true,
    line1: 'Jennifer',
    line2: 'K.',
    value: '+$4,200',
    age: '14 mo ago',
    lost: false,
  },
  {
    initials: 'MC',
    multiline: false,
    name: 'Marcus C.',
    value: '+$1,850',
    age: '8 mo ago',
    lost: true,
  },
  {
    initials: 'SR',
    multiline: false,
    name: 'Sarah R.',
    value: '+$3,100',
    age: '11 mo ago',
    lost: false,
  },
  {
    initials: 'DT',
    multiline: false,
    name: 'David T.',
    value: '+$2,400',
    age: '6 mo ago',
    lost: true,
  },
];

const CONNECTED_TOOLS = [
  { id: 's6t0', label: 'HubSpot', mark: 'HS', val: '+$420', num: 420 },
  { id: 's6t1', label: 'Calendly', mark: 'CAL', val: '+$380', num: 380 },
  { id: 's6t2', label: 'Gmail', mark: 'GM', val: '+$290', num: 290 },
  { id: 's6t3', label: 'Salesforce', mark: 'SF', val: '+$510', num: 510 },
  { id: 's6t4', label: 'WhatsApp', mark: 'WA', val: '+$180', num: 180 },
  { id: 's6t5', label: 'Instagram', mark: 'IG', val: '+$340', num: 340 },
];

const S6_CHAOS_PAIRS = [
  [0, 1],
  [1, 3],
  [3, 5],
  [5, 4],
  [4, 2],
  [2, 0],
  [0, 3],
  [1, 4],
  [2, 5],
].map((p, i) => ({ a: p[0], b: p[1], seed: i * 1.37, drift: i % 2 ? 1 : -1 }));

const S6_SPARK_TEXTS = ['sync error', 'missed', '404', 'timeout', 'duplicate', 'no data'];

/** Mini illustrations for each problem card (light background). */
export function PainCardViz({ index }) {
  switch (index) {
    case 0:
      return <SceneMissedCall />;
    case 1:
      return <SceneRace />;
    case 2:
      return <SceneAdmin />;
    case 3:
      return <SceneCold />;
    case 4:
      return <SceneLapsed />;
    case 5:
      return <SceneTools />;
    default:
      return null;
  }
}

function SceneMissedCall() {
  const r1 = useRef(null);
  const r2 = useRef(null);
  const r3 = useRef(null);
  const timeoutsRef = useRef([]);
  const typingIntervalRef = useRef(null);
  const mountedRef = useRef(true);

  const [missedOn, setMissedOn] = useState(false);
  const [catchOn, setCatchOn] = useState(false);
  const [smsOn, setSmsOn] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const [deliveredOn, setDeliveredOn] = useState(false);
  const [typingCursor, setTypingCursor] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    timeoutsRef.current = [];

    const st = (fn, ms) => {
      const id = window.setTimeout(() => {
        if (!mountedRef.current) return;
        fn();
      }, ms);
      timeoutsRef.current.push(id);
    };

    function animRing(el, delay) {
      if (!el) return;
      st(() => {
        el.style.transition = 'none';
        el.style.transform = 'translate(-50%,-50%) scale(0.4)';
        el.style.opacity = '0';
        requestAnimationFrame(() => {
          if (!mountedRef.current) return;
          el.style.transition = 'transform 1.6s ease-out, opacity 1.6s ease-out';
          el.style.transform = 'translate(-50%,-50%) scale(2.4)';
          el.style.opacity = '0';
          requestAnimationFrame(() => {
            if (!mountedRef.current) return;
            el.style.opacity = '0.9';
            st(() => {
              el.style.opacity = '0';
            }, 400);
          });
        });
      }, delay);
    }

    function typeText(text, speed, onDone) {
      let i = 0;
      setSmsBody('');
      setTypingCursor(true);
      if (typingIntervalRef.current) {
        window.clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
      typingIntervalRef.current = window.setInterval(() => {
        if (!mountedRef.current) {
          if (typingIntervalRef.current) {
            window.clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
          }
          return;
        }
        i += 1;
        setSmsBody(text.slice(0, i));
        if (i >= text.length) {
          if (typingIntervalRef.current) {
            window.clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
          }
          setTypingCursor(false);
          onDone();
        }
      }, speed);
    }

    function run() {
      if (!mountedRef.current) return;

      setMissedOn(false);
      setCatchOn(false);
      setSmsOn(false);
      setSmsBody('');
      setDeliveredOn(false);
      setTypingCursor(false);

      animRing(r1.current, 0);
      animRing(r2.current, 400);
      animRing(r3.current, 800);

      st(() => setMissedOn(true), 1800);
      st(() => setCatchOn(true), 2600);
      st(() => setSmsOn(true), 3200);
      st(() => {
        typeText(MISSED_CALL_SMS_MSG, 22, () => {
          st(() => setDeliveredOn(true), 300);
          st(() => run(), 3000);
        });
      }, 3500);
    }

    run();

    return () => {
      mountedRef.current = false;
      timeoutsRef.current.forEach((id) => window.clearTimeout(id));
      timeoutsRef.current = [];
      if (typingIntervalRef.current) {
        window.clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
    };
  }, []);

  return (
    <div
      className="pain-s1v2"
      role="img"
      aria-label="Missed call: ringing phone, missed call badge, Cure8 catches it, auto SMS follow-up types and delivers."
    >
      <div className={`pain-s1v2-catch ${catchOn ? 'is-on' : ''}`}>cure8 · caught it</div>
      <div className={`pain-s1v2-missed ${missedOn ? 'is-on' : ''}`}>missed call</div>

      <div ref={r1} className="pain-s1v2-ring pain-s1v2-ring--1" aria-hidden="true" />
      <div ref={r2} className="pain-s1v2-ring pain-s1v2-ring--2" aria-hidden="true" />
      <div ref={r3} className="pain-s1v2-ring pain-s1v2-ring--3" aria-hidden="true" />

      <div className="pain-s1v2-phone" aria-hidden="true" />

      <div className={`pain-s1v2-sms ${smsOn ? 'is-on' : ''}`}>
        <div className="pain-s1v2-sms-from">
          <span>To: +1 (905) 555-0182</span>
          <span className="pain-s1v2-sms-tag">auto · 47s</span>
        </div>
        <div className="pain-s1v2-sms-text">
          {smsBody}
          {typingCursor ? <span className="pain-s1v2-cursor" aria-hidden="true" /> : null}
        </div>
        <div className={`pain-s1v2-sms-time ${deliveredOn ? 'is-on' : ''}`}>
          <span className="pain-s1v2-sms-check">✓✓</span>
          <span>delivered</span>
        </div>
      </div>
    </div>
  );
}

function SceneRace() {
  const mountedRef = useRef(true);
  const timersRef = useRef([]);
  const rafRef = useRef(null);
  const notifIdRef = useRef(0);

  const [ui, setUi] = useState(() => ({
    pillRed: true,
    pillText: 'without cure8',
    statShow: false,
    statVal: '0:00',
    statTone: 'red',
    statLine1: 'hours without',
    statLine2: 'a response',
    tagShow: false,
    tagTone: 'red',
    tagText: 'no reply sent',
    phTime: '10:47 PM',
    notifs: [],
    outcomeShow: false,
    outcomeWon: false,
    outcomeText: '',
  }));

  const clearTimers = () => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    if (rafRef.current != null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const st = (fn, ms) => {
    const id = window.setTimeout(() => {
      if (!mountedRef.current) return;
      fn();
    }, ms);
    timersRef.current.push(id);
  };

  const animCountTo3h = (durationMs) => {
    const start = performance.now();
    const tick = (ts) => {
      if (!mountedRef.current) return;
      const p = Math.min((ts - start) / durationMs, 1);
      const ease = 1 - (1 - p) ** 2;
      const totalM = Math.round(ease * (3 * 60));
      const h = Math.floor(totalM / 60);
      const m = totalM % 60;
      setUi((u) => ({ ...u, statVal: `${h}:${m < 10 ? '0' : ''}${m}` }));
      if (p < 1) {
        rafRef.current = window.requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = window.requestAnimationFrame(tick);
  };

  useEffect(() => {
    mountedRef.current = true;

    const pushNotif = (n) => {
      const id = ++notifIdRef.current;
      setUi((u) => ({ ...u, notifs: [{ id, ...n }, ...u.notifs] }));
    };

    const runAfter = () => {
      clearTimers();
      setUi({
        pillRed: false,
        pillText: 'with cure8',
        statShow: false,
        statVal: '52s',
        statTone: 'grn',
        statLine1: 'response',
        statLine2: 'time',
        tagShow: false,
        tagTone: 'grn',
        tagText: 'cure8 replied',
        phTime: '10:47 PM',
        notifs: [],
        outcomeShow: false,
        outcomeWon: false,
        outcomeText: '',
      });

      st(() => {
        pushNotif({
          variant: 'red',
          app: 'Web Form',
          title: 'New inquiry',
          body: '"How much is a consultation? Anything this week?"',
          time: '10:47 PM',
          tcol: 'red',
        });
      }, 400);

      st(() => {
        setUi((u) => ({
          ...u,
          phTime: '10:48 PM',
          statShow: true,
          tagShow: true,
          notifs: [
            {
              id: ++notifIdRef.current,
              variant: 'grn',
              app: 'Cure8 · auto',
              title: 'Replied in 52 seconds',
              body: '"We have Thu + Fri open — want a link?"',
              time: '52s later',
              tcol: 'grn',
            },
            ...u.notifs,
          ],
        }));
      }, 1100);

      st(() => {
        pushNotif({
          variant: 'grn',
          app: 'Your Booking',
          title: 'New booking ✓',
          body: 'Jennifer — consultation confirmed',
          time: 'just now',
          tcol: 'grn',
        });
      }, 2400);

      st(() => {
        setUi((u) => ({
          ...u,
          outcomeShow: true,
          outcomeWon: true,
          outcomeText: 'Lead recovered — booked in 52 seconds',
        }));
      }, 3100);

      st(runBefore, 5200);
    };

    const runBefore = () => {
      clearTimers();
      setUi({
        pillRed: true,
        pillText: 'without cure8',
        statShow: false,
        statVal: '0:00',
        statTone: 'red',
        statLine1: 'hours without',
        statLine2: 'a response',
        tagShow: false,
        tagTone: 'red',
        tagText: 'no reply sent',
        phTime: '10:47 PM',
        notifs: [],
        outcomeShow: false,
        outcomeWon: false,
        outcomeText: '',
      });

      st(() => {
        const id = ++notifIdRef.current;
        setUi((u) => ({
          ...u,
          statShow: true,
          notifs: [
            {
              id,
              variant: 'red',
              app: 'Web Form',
              title: 'New inquiry',
              body: '"How much is a consultation? Anything this week?"',
              time: '10:47 PM',
              tcol: 'red',
            },
            ...u.notifs,
          ],
        }));
        animCountTo3h(4000);
      }, 400);

      st(() => setUi((u) => ({ ...u, tagShow: true })), 900);

      st(() => {
        setUi((u) => ({
          ...u,
          phTime: '11:47 PM',
          notifs: [
            {
              id: ++notifIdRef.current,
              variant: 'grey',
              app: 'Web Form',
              title: 'No reply sent',
              body: 'Waiting 1 hour — unanswered',
              time: '1 hr ago',
              tcol: 'dim',
            },
            ...u.notifs,
          ],
        }));
      }, 1800);

      st(() => {
        setUi((u) => ({
          ...u,
          phTime: '1:47 AM',
          notifs: [
            {
              id: ++notifIdRef.current,
              variant: 'grey',
              app: 'Competitor',
              title: 'Booking confirmed ✓',
              body: 'Jennifer booked elsewhere',
              time: 'just now',
              tcol: 'dim',
            },
            ...u.notifs,
          ],
        }));
      }, 3000);

      st(() => {
        setUi((u) => ({
          ...u,
          outcomeShow: true,
          outcomeWon: false,
          outcomeText: 'Lead lost — booked with competitor',
        }));
      }, 3900);

      st(runAfter, 5800);
    };

    st(runBefore, 200);

    return () => {
      mountedRef.current = false;
      clearTimers();
    };
  }, []);

  return (
    <div
      className="pain-s2v2"
      role="img"
      aria-label="Slow response: hours pass without a reply and the lead books elsewhere; with Cure8, reply in seconds and book."
    >
      <div className="pain-s2v2-hdr">
        <span
          className={`pain-s2v2-pill ${ui.pillRed ? 'pain-s2v2-pill--red' : 'pain-s2v2-pill--grn'}`}
        >
          {ui.pillText}
        </span>
      </div>

      <div className="pain-s2v2-viz">
        <div className="pain-s2v2-left">
          <div className={`pain-s2v2-stat ${ui.statShow ? 'is-on' : ''}`}>
            <div
              className={`pain-s2v2-stat-val ${ui.statTone === 'red' ? 'pain-s2v2-stat-val--red' : 'pain-s2v2-stat-val--grn'}`}
            >
              {ui.statVal}
            </div>
            <div className="pain-s2v2-stat-lbl">
              {ui.statLine1}
              <br />
              {ui.statLine2}
            </div>
          </div>
          <div
            className={`pain-s2v2-tag ${ui.tagTone === 'red' ? 'pain-s2v2-tag--red' : 'pain-s2v2-tag--grn'} ${ui.tagShow ? 'is-on' : ''}`}
          >
            <span className="pain-s2v2-tag-dot" aria-hidden="true" />
            <span>{ui.tagText}</span>
          </div>
        </div>

        <div className="pain-s2v2-phone">
          <div className="pain-s2v2-ph-top">
            <div className="pain-s2v2-ph-notch" aria-hidden="true" />
            <div className="pain-s2v2-ph-time">{ui.phTime}</div>
          </div>
          <div className="pain-s2v2-ph-body">
            {ui.notifs.map((n) => (
              <div
                key={n.id}
                className={`pain-s2v2-notif pain-s2v2-notif--${n.variant}`}
              >
                <div className="pain-s2v2-n-hdr">
                  <span
                    className={`pain-s2v2-n-app pain-s2v2-n-app--${n.tcol === 'dim' ? 'dim' : n.tcol}`}
                  >
                    {n.app}
                  </span>
                  <span
                    className={`pain-s2v2-n-dot pain-s2v2-n-dot--${n.tcol === 'dim' ? 'dim' : n.tcol}`}
                    aria-hidden="true"
                  />
                </div>
                <div className="pain-s2v2-n-title">{n.title}</div>
                <div className="pain-s2v2-n-body">{n.body}</div>
                <div className={`pain-s2v2-n-time pain-s2v2-n-time--${n.tcol === 'dim' ? 'dim' : n.tcol}`}>{n.time}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div
        className={`pain-s2v2-outcome ${ui.outcomeShow ? 'is-on' : ''} ${ui.outcomeWon ? 'pain-s2v2-outcome--won' : 'pain-s2v2-outcome--lost'}`}
      >
        <span className="pain-s2v2-outcome-dot" aria-hidden="true" />
        <span>{ui.outcomeText}</span>
      </div>
    </div>
  );
}

function SceneAdmin() {
  const [phase, setPhase] = useState('idle');
  const [particles, setParticles] = useState([]);
  const timeoutsRef = useRef([]);
  const intervalRef = useRef(null);
  const particleIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    timeoutsRef.current = [];

    const st = (fn, ms) => {
      const id = window.setTimeout(() => {
        if (!mountedRef.current) return;
        fn();
      }, ms);
      timeoutsRef.current.push(id);
    };

    const spawnParticle = () => {
      const angle = Math.random() * Math.PI * 2;
      const radius = 42;
      const id = particleIdRef.current++;
      const amount = ADMIN_LOSS_AMOUNTS[Math.floor(Math.random() * ADMIN_LOSS_AMOUNTS.length)];
      const startX = 40 + Math.cos(angle) * radius;
      const startY = 40 + Math.sin(angle) * radius;
      const dx = Math.cos(angle) * (20 + Math.random() * 24);
      const dy = Math.sin(angle) * (18 + Math.random() * 20) - 8;
      const duration = 900 + Math.random() * 500;

      setParticles((prev) => [...prev, { id, amount, startX, startY, dx, dy, duration }]);
      const removeId = window.setTimeout(() => {
        if (!mountedRef.current) return;
        setParticles((prev) => prev.filter((p) => p.id !== id));
      }, duration + 120);
      timeoutsRef.current.push(removeId);
    };

    const reset = () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setParticles([]);
      setPhase('idle');
      st(run, 500);
    };

    const run = () => {
      setParticles([]);
      setPhase('running');
      st(() => setPhase('task1'), 200);
      st(() => setPhase('task2'), 420);
      st(() => setPhase('task3'), 640);

      spawnParticle();
      intervalRef.current = window.setInterval(spawnParticle, 320);
      st(reset, 6400);
    };

    run();

    return () => {
      mountedRef.current = false;
      timeoutsRef.current.forEach((id) => window.clearTimeout(id));
      timeoutsRef.current = [];
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return (
    <div className={`pain-s3-wrap ${phase === 'running' || phase === 'task1' || phase === 'task2' || phase === 'task3' ? 'is-running' : ''}`}>
      <div className="pain-s3-clock-wrap">
        <div className="pain-s3-particles" aria-hidden="true">
          {particles.map((p) => (
            <span
              key={p.id}
              className="pain-s3-particle"
              style={{
                left: `${p.startX}px`,
                top: `${p.startY}px`,
                '--dx': `${p.dx}px`,
                '--dy': `${p.dy}px`,
                '--pdur': `${p.duration}ms`,
              }}
            >
              {p.amount}
            </span>
          ))}
        </div>
        <svg viewBox="0 0 80 80" fill="none" className="pain-s3-clock-svg" aria-hidden="true">
          <circle cx="40" cy="40" r="36" className="pain-s3-face" />
          <line x1="40" y1="6" x2="40" y2="12" className="pain-s3-tick" />
          <line x1="40" y1="68" x2="40" y2="74" className="pain-s3-tick" />
          <line x1="6" y1="40" x2="12" y2="40" className="pain-s3-tick" />
          <line x1="68" y1="40" x2="74" y2="40" className="pain-s3-tick" />
          <circle cx="40" cy="40" r="30" className="pain-s3-arc" />
          <line x1="40" y1="40" x2="40" y2="24" className="pain-s3-hour-hand" />
          <line x1="40" y1="40" x2="40" y2="16" className="pain-s3-min-hand" />
          <circle cx="40" cy="40" r="3" className="pain-s3-center-dot" />
          <circle cx="40" cy="40" r="1.5" className="pain-s3-center-dot-red" />
        </svg>
      </div>
      <div className="pain-s3-tasks">
        <div className={`pain-s3-task ${phase === 'task1' || phase === 'task2' || phase === 'task3' ? 'is-show' : ''}`}>
          <div className="pain-s3-task-dot" />
          Send reminder to client
        </div>
        <div className={`pain-s3-task ${phase === 'task2' || phase === 'task3' ? 'is-show' : ''}`}>
          <div className="pain-s3-task-dot" />
          Chase docs for Sadiq
        </div>
        <div className={`pain-s3-task ${phase === 'task3' ? 'is-show' : ''}`}>
          <div className="pain-s3-task-dot" />
          Follow up on quote
        </div>
      </div>
    </div>
  );
}

function SceneCold() {
  const mountedRef = useRef(true);
  const timersRef = useRef([]);
  const rafRef = useRef(null);

  const [ui, setUi] = useState(() => ({
    phaseBefore: true,
    phaseAfter: false,
    tbarPct: 0,
    tbarGreen: false,
    tm: [false, false, false, false],
    msg0: false,
    msg1: false,
    s0: false,
    s1: false,
    s2: false,
    s3: false,
    timerBox: false,
    timerVal: '0h',
    leadPhase: 'warm',
    showLead: true,
  }));

  const st = (fn, ms) => {
    const id = window.setTimeout(() => {
      if (!mountedRef.current) return;
      fn();
    }, ms);
    timersRef.current.push(id);
  };

  const clearTimers = () => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const resetBeforeState = () => ({
    phaseBefore: true,
    phaseAfter: false,
    tbarPct: 0,
    tbarGreen: false,
    tm: [false, false, false, false],
    msg0: false,
    msg1: false,
    s0: false,
    s1: false,
    s2: false,
    s3: false,
    timerBox: false,
    timerVal: '0h',
    leadPhase: 'warm',
    showLead: true,
  });

  useEffect(() => {
    mountedRef.current = true;

    function runBarRed(onDone) {
      const start = performance.now();
      const dur = 3000;
      function tick(ts) {
        if (!mountedRef.current) return;
        const pct = Math.min((ts - start) / dur, 1);
        const width = pct * 80;
        const hours = Math.round(pct * 18);
        setUi((u) => ({
          ...u,
          tbarPct: width,
          timerVal: `${hours}h`,
          leadPhase: pct > 0.4 ? 'cold' : 'warm',
        }));
        if (pct < 1) {
          rafRef.current = window.requestAnimationFrame(tick);
        } else {
          rafRef.current = null;
          setUi((u) => ({
            ...u,
            tm: [u.tm[0], u.tm[1], u.tm[2], true],
          }));
          st(() => {
            setUi((u) => ({ ...u, showLead: false, leadPhase: 'gone' }));
          }, 200);
          st(() => setUi((u) => ({ ...u, s1: true })), 400);
          st(onDone, 2200);
        }
      }
      rafRef.current = window.requestAnimationFrame(tick);
    }

    function runBarGreen(onDone) {
      const start = performance.now();
      const dur = 1000;
      function tick(ts) {
        if (!mountedRef.current) return;
        const pct = Math.min((ts - start) / dur, 1);
        const width = pct * 20;
        setUi((u) => ({ ...u, tbarPct: width }));
        if (pct < 1) {
          rafRef.current = window.requestAnimationFrame(tick);
        } else {
          rafRef.current = null;
          setUi((u) => ({ ...u, s2: true }));
          st(() => setUi((u) => ({ ...u, msg1: true })), 200);
          st(() => {
            setUi((u) => ({ ...u, leadPhase: 'recovered' }));
            st(() => setUi((u) => ({ ...u, s3: true })), 300);
          }, 800);
          st(onDone, 3500);
        }
      }
      rafRef.current = window.requestAnimationFrame(tick);
    }

    function runAfter() {
      clearTimers();
      setUi({
        phaseBefore: false,
        phaseAfter: true,
        tbarPct: 0,
        tbarGreen: true,
        tm: [false, false, false, false],
        msg0: false,
        msg1: false,
        s0: false,
        s1: false,
        s2: false,
        s3: false,
        timerBox: false,
        timerVal: '0h',
        leadPhase: 'warm',
        showLead: true,
      });

      st(() => setUi((u) => ({ ...u, msg0: true })), 300);

      st(() => {
        runBarGreen(() => {
          clearTimers();
          st(runBefore, 400);
        });
      }, 700);
    }

    function runBefore() {
      clearTimers();
      setUi(resetBeforeState());

      st(() => {
        setUi((u) => ({ ...u, tm: [true, u.tm[1], u.tm[2], u.tm[3]] }));
        st(() => setUi((u) => ({ ...u, msg0: true })), 100);
        st(() => setUi((u) => ({ ...u, s0: true })), 300);
      }, 300);

      st(() => {
        st(() => {
          setUi((u) => ({ ...u, tm: [u.tm[0], true, u.tm[2], u.tm[3]] }));
        }, 500);
        st(() => {
          setUi((u) => ({ ...u, tm: [u.tm[0], u.tm[1], true, u.tm[3]] }));
        }, 1000);
        st(() => setUi((u) => ({ ...u, timerBox: true })), 200);
        runBarRed(runAfter);
      }, 600);
    }

    st(runBefore, 200);

    return () => {
      mountedRef.current = false;
      clearTimers();
    };
  }, []);

  const leadClass =
    ui.leadPhase === 'warm'
      ? 'pain-s4v2-lead--warm'
      : ui.leadPhase === 'cold'
        ? 'pain-s4v2-lead--cold'
        : ui.leadPhase === 'recovered'
          ? 'pain-s4v2-lead--recovered'
          : 'pain-s4v2-lead--gone';

  return (
    <div
      className="pain-s4v2"
      role="img"
      aria-label="Lead timeline: without response the lead goes cold; with Cure8, fast reply recovers the lead."
    >
      <div className="pain-s4v2-phase-row">
        <span className={`pain-s4v2-plabel pain-s4v2-plabel--before ${ui.phaseBefore ? 'is-on' : ''}`}>
          without cure8
        </span>
        <span className={`pain-s4v2-plabel pain-s4v2-plabel--after ${ui.phaseAfter ? 'is-on' : ''}`}>
          with cure8
        </span>
      </div>

      <div className="pain-s4v2-tbar">
        <div
          className={`pain-s4v2-tbar-fill ${ui.tbarGreen ? 'pain-s4v2-tbar-fill--green' : ''}`}
          style={{ width: `${ui.tbarPct}%` }}
        />
      </div>

      <div className={`pain-s4v2-tmark pain-s4v2-tmark--0 ${ui.tm[0] ? 'is-on' : ''}`}>now</div>
      <div className={`pain-s4v2-tmark pain-s4v2-tmark--1 ${ui.tm[1] ? 'is-on' : ''}`}>6 hrs</div>
      <div className={`pain-s4v2-tmark pain-s4v2-tmark--2 ${ui.tm[2] ? 'is-on' : ''}`}>18 hrs</div>
      <div className={`pain-s4v2-tmark pain-s4v2-tmark--3 ${ui.tm[3] ? 'is-on' : ''}`}>gone</div>

      <div className={`pain-s4v2-lead ${leadClass} ${ui.showLead ? '' : 'pain-s4v2-lead--hidden'}`}>
        <div className="pain-s4v2-lead-avatar">JK</div>
        <div className="pain-s4v2-lead-name">Jennifer</div>
      </div>

      <div className={`pain-s4v2-msg ${ui.msg0 ? 'is-on' : ''}`}>&ldquo;How much for a consultation?&rdquo;</div>

      <div className={`pain-s4v2-status pain-s4v2-status--red pain-s4v2-status--tr ${ui.s0 && !ui.phaseAfter ? 'is-on' : ''}`}>
        no reply
      </div>
      <div className={`pain-s4v2-status pain-s4v2-status--green pain-s4v2-status--tr ${ui.s2 ? 'is-on' : ''}`}>
        replied · 52s
      </div>

      <div className={`pain-s4v2-timer ${ui.timerBox ? 'is-on' : ''}`}>
        <div className={`pain-s4v2-timer-val ${ui.tbarGreen ? 'pain-s4v2-timer-val--green' : ''}`}>{ui.timerVal}</div>
        <div className="pain-s4v2-timer-lbl">without response</div>
      </div>

      <div className={`pain-s4v2-status pain-s4v2-status--red pain-s4v2-status--bc ${ui.s1 ? 'is-on' : ''}`}>
        lead went cold
      </div>
      <div className={`pain-s4v2-status pain-s4v2-status--green pain-s4v2-status--bc ${ui.s3 ? 'is-on' : ''}`}>
        lead recovered
      </div>

      <div className={`pain-s4v2-msg pain-s4v2-msg--cure8 ${ui.msg1 ? 'is-on' : ''}`}>
        &ldquo;We have Thu + Fri open — want a booking link?&rdquo;
      </div>
    </div>
  );
}

function SceneLapsed() {
  const mountedRef = useRef(true);
  const timersRef = useRef([]);
  const vizRef = useRef(null);
  const rowRefs = useRef([]);
  const popIdRef = useRef(0);

  const [shown, setShown] = useState(() => LAPSED_ROWS.map(() => false));
  const [muted, setMuted] = useState(() => LAPSED_ROWS.map(() => false));
  const [scanning, setScanning] = useState(null);
  const [done, setDone] = useState(() => LAPSED_ROWS.map(() => false));
  const [pops, setPops] = useState([]);

  useEffect(() => {
    mountedRef.current = true;

    const clearTimers = () => {
      timersRef.current.forEach((id) => window.clearTimeout(id));
      timersRef.current = [];
    };

    const st = (fn, ms) => {
      const id = window.setTimeout(() => {
        if (!mountedRef.current) return;
        fn();
      }, ms);
      timersRef.current.push(id);
    };

    const addPop = (rowIndex) => {
      const viz = vizRef.current;
      const row = rowRefs.current[rowIndex];
      if (!viz || !row) return;
      const vr = viz.getBoundingClientRect();
      const rr = row.getBoundingClientRect();
      const left = rr.right - vr.left - 88;
      const top = rr.top - vr.top + 4;
      const key = ++popIdRef.current;
      const amount = LAPSED_ROWS[rowIndex].value;
      setPops((p) => [...p, { key, left, top, amount }]);
      st(
        () => setPops((p) => p.filter((x) => x.key !== key)),
        1000,
      );
    };

    const resetState = () => {
      setShown(LAPSED_ROWS.map(() => false));
      setMuted(LAPSED_ROWS.map(() => false));
      setScanning(null);
      setDone(LAPSED_ROWS.map(() => false));
      setPops([]);
    };

    const run = () => {
      clearTimers();
      resetState();

      LAPSED_ROWS.forEach((_, i) => {
        st(() => {
          setShown((prev) => {
            const next = [...prev];
            next[i] = true;
            return next;
          });
        }, 160 + i * 170);
      });

      LAPSED_ROWS.forEach((_, i) => {
        const base = 1180 + i * 980;
        st(() => {
          setMuted(LAPSED_ROWS.map((_, j) => j !== i));
          setScanning(i);
        }, base);
        st(() => {
          setScanning(null);
          setDone((prev) => {
            const next = [...prev];
            next[i] = true;
            return next;
          });
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => addPop(i));
          });
        }, base + 700);
      });

      st(() => {
        setMuted(LAPSED_ROWS.map(() => false));
      }, 1180 + LAPSED_ROWS.length * 980 + 400);

      st(run, 1180 + LAPSED_ROWS.length * 980 + 3000);
    };

    st(run, 250);

    return () => {
      mountedRef.current = false;
      clearTimers();
    };
  }, []);

  return (
    <div
      ref={vizRef}
      className="pain-s5v2"
      role="img"
      aria-label="Past clients with dormant revenue: each row reveals hidden value when scanned."
    >
      <div className="pain-s5v2-rows">
        {LAPSED_ROWS.map((row, i) => (
          <div
            key={row.initials + row.age}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
            className={[
              'pain-s5v2-row',
              shown[i] ? 'pain-s5v2-row--show' : '',
              muted[i] ? 'pain-s5v2-row--muted' : '',
              scanning === i ? 'pain-s5v2-row--active pain-s5v2-row--scanning' : '',
              done[i] ? 'pain-s5v2-row--done' : '',
              row.lost ? 'pain-s5v2-row--lost' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className="pain-s5v2-av">{row.initials}</div>
            <div className="pain-s5v2-name">
              {row.multiline ? (
                <>
                  {row.line1}
                  <br />
                  {row.line2}
                </>
              ) : (
                row.name
              )}
            </div>
            <div className="pain-s5v2-right">
              <div className="pain-s5v2-value">{row.value}</div>
              <div className="pain-s5v2-age">{row.age}</div>
            </div>
          </div>
        ))}
      </div>

      {pops.map((p) => (
        <div
          key={p.key}
          className="pain-s5v2-pop"
          style={{ left: p.left, top: p.top }}
        >
          {p.amount}
        </div>
      ))}
    </div>
  );
}

function SceneTools() {
  const vizRef = useRef(null);
  const mountedRef = useRef(true);
  const phaseRef = useRef('init');
  const transStartRef = useRef(0);
  const startRef = useRef(0);
  const engTotalRef = useRef(0);
  const rafRef = useRef(null);
  const timersRef = useRef([]);
  const packetIntervalRef = useRef(null);
  const errIntervalRef = useRef(null);

  const [pillGood, setPillGood] = useState(false);
  const [pillText, setPillText] = useState('Disconnected');
  const [outcomeGood, setOutcomeGood] = useState(false);
  const [outcomeIcon, setOutcomeIcon] = useState('!');
  const [outcomeText, setOutcomeText] = useState('No sync — someone copies data manually, every time.');
  const [outcomeVisible, setOutcomeVisible] = useState(true);
  const [engineVisible, setEngineVisible] = useState(false);
  const [engineOn, setEngineOn] = useState(false);
  const [engVal, setEngVal] = useState('$0');
  const [nodes, setNodes] = useState(() => CONNECTED_TOOLS.map(() => ({ shake: true, on: false })));

  useEffect(() => {
    const viz = vizRef.current;
    const cvs = viz.querySelector('canvas');
    if (!viz || !cvs) return;
    const ctx = cvs.getContext('2d');
    mountedRef.current = true;
    /** Below 1 slows timers, packet travel, and error popups (slightly calmer loop). */
    const speed = 0.82;

    const easeOut = (x) => 1 - (1 - x) ** 3;
    const easeInOut = (x) => (x < 0.5 ? 2 * x * x : 1 - (-2 * x + 2) ** 2 / 2);

    const hexA_rgb = (c, a) => {
      if (c.startsWith('rgba')) return c;
      const h = c.replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r},${g},${b},${a})`;
    };

    const cv = (name) => getComputedStyle(viz).getPropertyValue(name).trim();

    const W = () => viz.clientWidth;
    const H = () => viz.clientHeight;
    const C = () => ({ x: W() / 2, y: H() / 2 });

    const toolPositions = () => {
      const w = W();
      const h = H();
      const padX = Math.min(68, w * 0.22);
      const padY = Math.min(34, h * 0.2);
      return [
        { x: padX, y: padY },
        { x: w - padX, y: padY },
        { x: padX, y: h / 2 },
        { x: w - padX, y: h / 2 },
        { x: padX + 4, y: h - padY },
        { x: w - padX - 4, y: h - padY },
      ];
    };

    const placeToolsApp = () => {
      const P = toolPositions();
      CONNECTED_TOOLS.forEach((_, i) => {
        const el = viz.querySelector(`[data-s6-tool="${i}"]`);
        if (el) {
          el.style.left = `${P[i].x}px`;
          el.style.top = `${P[i].y}px`;
        }
      });
    };

    const ctr = (id) => {
      const i = CONNECTED_TOOLS.findIndex((t) => t.id === id);
      return toolPositions()[i];
    };

    const sizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      cvs.width = viz.clientWidth * dpr;
      cvs.height = viz.clientHeight * dpr;
      cvs.style.width = `${viz.clientWidth}px`;
      cvs.style.height = `${viz.clientHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const clearTimers = () => {
      timersRef.current.forEach((id) => window.clearTimeout(id));
      timersRef.current = [];
      if (packetIntervalRef.current) {
        window.clearInterval(packetIntervalRef.current);
        packetIntervalRef.current = null;
      }
      if (errIntervalRef.current) {
        window.clearInterval(errIntervalRef.current);
        errIntervalRef.current = null;
      }
    };

    const later = (fn, ms) => {
      const id = window.setTimeout(() => {
        if (!mountedRef.current) return;
        fn();
      }, ms / speed);
      timersRef.current.push(id);
    };

    const drawChaos = (t) => {
      const bad = cv('--pain-s6-bad');
      S6_CHAOS_PAIRS.forEach((pair, i) => {
        const a = ctr(CONNECTED_TOOLS[pair.a].id);
        const b = ctr(CONNECTED_TOOLS[pair.b].id);
        const jx = Math.sin(t * 1.1 + pair.seed) * 10;
        const jy = Math.cos(t * 0.9 + pair.seed * 1.3) * 8;
        const mx = (a.x + b.x) / 2 + jx + pair.drift * 6;
        const my = (a.y + b.y) / 2 + jy;
        ctx.save();
        ctx.strokeStyle = hexA_rgb(bad, 0.28);
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 5]);
        ctx.lineDashOffset = -t * 14 + i * 3;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(mx, my, b.x, b.y);
        ctx.stroke();
        ctx.restore();
        const pulse = (Math.sin(t * 2 + pair.seed * 2) + 1) / 2;
        if (pulse > 0.92) {
          ctx.save();
          ctx.fillStyle = hexA_rgb(bad, 0.35);
          ctx.beginPath();
          ctx.arc(mx, my, 3 + pulse * 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      });
    };

    const drawTransition = (t, p) => {
      const bad = cv('--pain-s6-bad');
      const good = cv('--pain-s6-good');
      const C0 = C();
      S6_CHAOS_PAIRS.forEach((pair, i) => {
        const a = ctr(CONNECTED_TOOLS[pair.a].id);
        const b = ctr(CONNECTED_TOOLS[pair.b].id);
        const mx0 = (a.x + b.x) / 2 + Math.sin(t * 1.1 + pair.seed) * 10 + pair.drift * 6;
        const my0 = (a.y + b.y) / 2 + Math.cos(t * 0.9 + pair.seed * 1.3) * 8;
        const pullP = easeInOut(Math.min(p * 1.4, 1));
        const mx = mx0 + (C0.x - mx0) * pullP;
        const my = my0 + (C0.y - my0) * pullP;
        ctx.save();
        ctx.strokeStyle = hexA_rgb(bad, 0.28 * (1 - p));
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 5]);
        ctx.lineDashOffset = -t * 14 + i * 3;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(mx, my, b.x, b.y);
        ctx.stroke();
        ctx.restore();
      });
      const spokeP = easeOut(Math.max(0, (p - 0.25) / 0.75));
      CONNECTED_TOOLS.forEach((t_) => {
        const to = ctr(t_.id);
        const sx = C0.x + (to.x - C0.x) * spokeP;
        const sy = C0.y + (to.y - C0.y) * spokeP;
        ctx.save();
        ctx.strokeStyle = hexA_rgb(good, 0.5);
        ctx.lineWidth = 1.25;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(C0.x, C0.y);
        ctx.lineTo(sx, sy);
        ctx.stroke();
        ctx.restore();
      });
    };

    const drawFlow = (t) => {
      const good = cv('--pain-s6-good');
      const C0 = C();
      CONNECTED_TOOLS.forEach((t_, i) => {
        const to = ctr(t_.id);
        ctx.save();
        ctx.strokeStyle = hexA_rgb(good, 0.28);
        ctx.lineWidth = 1.25;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(to.x, to.y);
        ctx.lineTo(C0.x, C0.y);
        ctx.stroke();
        ctx.restore();
        const phase_ = (t * 0.6 + i * 0.17) % 1;
        const hx = to.x + (C0.x - to.x) * phase_;
        const hy = to.y + (C0.y - to.y) * phase_;
        const grad = ctx.createRadialGradient(hx, hy, 0, hx, hy, 12);
        grad.addColorStop(0, hexA_rgb(good, 0.55));
        grad.addColorStop(1, hexA_rgb(good, 0));
        ctx.save();
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(hx, hy, 12, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });
    };

    const animCount = (from, to, dur) => {
      const s = performance.now();
      const step = (ts) => {
        if (!mountedRef.current) return;
        const p = Math.min((ts - s) / dur, 1);
        const v = Math.round(from + (to - from) * easeOut(p));
        setEngVal(`+$${v.toLocaleString()}`);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const spawnPacket = (i) => {
      const t_ = CONNECTED_TOOLS[i];
      const from = ctr(t_.id);
      const to = C();
      const dot = document.createElement('div');
      dot.className = 'pain-s6v2-pkt';
      const lbl = document.createElement('div');
      lbl.className = 'pain-s6v2-pkt-lbl';
      lbl.textContent = t_.val;
      viz.appendChild(dot);
      viz.appendChild(lbl);
      const dur = 880 / speed;
      const s = performance.now();
      const stepPkt = (ts) => {
        if (!mountedRef.current) return;
        const p = Math.min((ts - s) / dur, 1);
        const e = easeOut(p);
        const x = from.x + (to.x - from.x) * e;
        const y = from.y + (to.y - from.y) * e;
        dot.style.left = `${x - 4}px`;
        dot.style.top = `${y - 4}px`;
        lbl.style.left = `${x + 10}px`;
        lbl.style.top = `${y - 8}px`;
        dot.style.opacity = p < 0.08 ? p / 0.08 : p > 0.88 ? (1 - p) / 0.12 : 1;
        lbl.style.opacity = p > 0.12 && p < 0.76 ? 1 : 0;
        lbl.style.transform = `translateY(${-p * 6}px)`;
        if (p < 1) requestAnimationFrame(stepPkt);
        else {
          dot.remove();
          lbl.remove();
          const prev = engTotalRef.current;
          engTotalRef.current += t_.num;
          animCount(prev, engTotalRef.current, 520 / speed);
        }
      };
      requestAnimationFrame(stepPkt);
    };

    const spawnErrLabel = () => {
      const pair = S6_CHAOS_PAIRS[Math.floor(Math.random() * S6_CHAOS_PAIRS.length)];
      const a = ctr(CONNECTED_TOOLS[pair.a].id);
      const b = ctr(CONNECTED_TOOLS[pair.b].id);
      const x = (a.x + b.x) / 2 + (Math.random() - 0.5) * 24;
      const y = (a.y + b.y) / 2 + (Math.random() - 0.5) * 16;
      const el = document.createElement('div');
      el.className = 'pain-s6v2-err-lbl';
      el.textContent = S6_SPARK_TEXTS[Math.floor(Math.random() * S6_SPARK_TEXTS.length)];
      el.style.left = `${x + 6}px`;
      el.style.top = `${y - 6}px`;
      viz.appendChild(el);
      requestAnimationFrame(() => {
        el.style.opacity = '1';
        el.style.transform = 'translateY(-4px)';
      });
      const tid1 = window.setTimeout(() => {
        el.style.opacity = '0';
      }, 900 / speed);
      const tid2 = window.setTimeout(() => {
        el.remove();
      }, 1400 / speed);
      timersRef.current.push(tid1, tid2);
    };

    const resetAll = () => {
      clearTimers();
      viz.querySelectorAll('.pain-s6v2-pkt, .pain-s6v2-pkt-lbl').forEach((e) => e.remove());
      viz.querySelectorAll('.pain-s6v2-err-lbl').forEach((e) => e.remove());
      engTotalRef.current = 0;
      setEngVal('$0');
      setNodes(CONNECTED_TOOLS.map(() => ({ shake: true, on: false })));
      setEngineOn(false);
      setEngineVisible(false);
    };

    const phaseFlowPackets = () => {
      let idx = 0;
      packetIntervalRef.current = window.setInterval(() => {
        spawnPacket(idx % CONNECTED_TOOLS.length);
        idx += 1;
      }, 440 / speed);
    };

    const phaseTransition = () => {
      if (packetIntervalRef.current) {
        window.clearInterval(packetIntervalRef.current);
        packetIntervalRef.current = null;
      }
      if (errIntervalRef.current) {
        window.clearInterval(errIntervalRef.current);
        errIntervalRef.current = null;
      }
      viz.querySelectorAll('.pain-s6v2-err-lbl').forEach((e) => {
        e.style.opacity = '0';
      });
      later(() => {
        viz.querySelectorAll('.pain-s6v2-err-lbl').forEach((e) => e.remove());
      }, 300);

      setNodes(CONNECTED_TOOLS.map(() => ({ shake: false, on: false })));

      phaseRef.current = 'transition';
      transStartRef.current = performance.now();

      later(() => setEngineVisible(true), 100);
      later(() => {
        setEngineOn(true);
        setPillGood(true);
        setPillText('Connected');
      }, 700);

      CONNECTED_TOOLS.forEach((_, i) => {
        later(() => {
          setNodes((prev) =>
            prev.map((n, j) => ({
              shake: false,
              on: j <= i,
            })),
          );
        }, 900 + i * 110);
      });

      later(phaseFlowPackets, 1700);

      later(() => {
        setOutcomeGood(true);
        setOutcomeIcon('✓');
        setOutcomeText('All tools synced — revenue recovered automatically.');
      }, 2100);

      later(phaseChaos, 5600);
    };

    const phaseChaos = () => {
      resetAll();
      phaseRef.current = 'chaos';
      startRef.current = 0;
      setPillGood(false);
      setPillText('Disconnected');
      setOutcomeGood(false);
      setOutcomeIcon('!');
      setOutcomeText('No sync — someone copies data manually, every time.');
      setOutcomeVisible(true);

      errIntervalRef.current = window.setInterval(spawnErrLabel, 700 / speed);

      later(phaseTransition, 3400);
    };

    const drawFrame = (ts) => {
      if (!mountedRef.current) return;
      if (!startRef.current) startRef.current = ts;
      const t = (ts - startRef.current) / 1000;
      placeToolsApp();
      sizeCanvas();
      ctx.clearRect(0, 0, viz.clientWidth, viz.clientHeight);

      const phase = phaseRef.current;
      if (phase === 'chaos') {
        drawChaos(t * speed);
      } else if (phase === 'transition') {
        const dur = 1.45 / speed;
        const p = Math.min((ts - transStartRef.current) / 1000 / dur, 1);
        drawTransition(t * speed, p);
        if (p >= 1) phaseRef.current = 'flow';
      } else if (phase === 'flow') {
        drawFlow(t * speed);
      }

      rafRef.current = window.requestAnimationFrame(drawFrame);
    };

    const onResize = () => {
      placeToolsApp();
      sizeCanvas();
    };
    window.addEventListener('resize', onResize);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onResize);
      ro.observe(viz);
    }

    requestAnimationFrame(() => {
      placeToolsApp();
      sizeCanvas();
    });

    rafRef.current = window.requestAnimationFrame(drawFrame);
    const kick = window.setTimeout(phaseChaos, 300 / speed);
    timersRef.current.push(kick);

    return () => {
      mountedRef.current = false;
      clearTimers();
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
      viz.querySelectorAll('.pain-s6v2-pkt, .pain-s6v2-pkt-lbl, .pain-s6v2-err-lbl').forEach((e) => e.remove());
    };
  }, []);

  return (
    <div
      className="pain-s6v2"
      role="img"
      aria-label="Disconnected tools with tangled sync, then Cure8 connects them and recovers revenue."
    >
      <div className="pain-s6v2-hdr">
        <span className={`pain-s6v2-pill ${pillGood ? 'pain-s6v2-pill--good' : 'pain-s6v2-pill--bad'}`}>
          <span className="pain-s6v2-pill-dot" aria-hidden="true" />
          {pillText}
        </span>
      </div>
      <div className="pain-s6v2-viz" ref={vizRef}>
        <canvas className="pain-s6v2-canvas" aria-hidden="true" />
        <div
          className={`pain-s6v2-engine ${engineVisible ? 'pain-s6v2-engine--visible' : ''} ${engineOn ? 'pain-s6v2-engine--on' : ''}`}
        >
          <div className="pain-s6v2-eng-ring" aria-hidden="true" />
          <div className="pain-s6v2-eng-ring2" aria-hidden="true" />
          <div className="pain-s6v2-eng-logo">
            CURE<span className="pain-s6v2-eng-x">8</span>
          </div>
          <div className="pain-s6v2-eng-val">{engVal}</div>
        </div>
        {CONNECTED_TOOLS.map((tool, i) => (
          <div
            key={tool.id}
            data-s6-tool={i}
            className={`pain-s6v2-tnode ${nodes[i]?.shake ? 'pain-s6v2-tnode--shake' : ''} ${nodes[i]?.on ? 'pain-s6v2-tnode--on' : ''}`}
          >
            <div className="pain-s6v2-mark">{tool.mark}</div>
            <span className="pain-s6v2-dot-status" aria-hidden="true" />
            <span>{tool.label}</span>
          </div>
        ))}
      </div>
      <div
        className={`pain-s6v2-outcome ${outcomeVisible ? (outcomeGood ? 'pain-s6v2-outcome--good' : 'pain-s6v2-outcome--bad') : 'pain-s6v2-outcome--hide'}`}
      >
        <div className="pain-s6v2-o-icon">{outcomeIcon}</div>
        <span>{outcomeText}</span>
      </div>
    </div>
  );
}
